import React, { useEffect, useState } from "react";
import {
  Spinner,
  Modal,
  Button,
  Table,
  Pagination,
  Col,
  Row,
  Card,
} from "react-bootstrap";
import { useQueryClient } from "@tanstack/react-query";
import { useGetActiveCompanies } from "../../api/queries/MuslimInvest/useGetActiveCompanies";
import { CompanyProfileData } from "../Models/CompanyProfileData";
import { BsEye, BsFileArrowDown, BsTrash } from "react-icons/bs";
import {
  FaBuilding,
  FaInfoCircle,
  FaIndustry,
  FaBullseye,
  FaBoxes,
  FaBoxOpen,
  FaGlobe,
  FaDollarSign,
  FaChartLine,
  FaPercentage,
  FaTag,
  FaFilePdf,
  FaUsers,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useSaveCompanyProfile } from "../../api/mutations/MuslimInvest/useSaveCompanyProfile";
import axiosInstance, { getAPIURL } from "../../api/axiosInstance";
import { FoundingTeamData } from "../Models/FoundingTeamData";
import { useDeleteCompany } from "../../api/mutations/MuslimInvest/useDeleteCompany";
import { useSendEmail } from "../../api/mutations/useSendEmail";
import { Users } from "../../types/Types";
import { useGetPitcheByCompanyId } from "../../api/queries/Pitch/useGetPitchByCompanyId";
import { PitchFormData } from "../Models/PitchFormData";
import { useGetPitchFiles } from "../../api/queries/Pitch/useGetPitchFiles";
import { useGetPitchTeamMembers } from "../../api/queries/Pitch/useGetPitchTeamMembers";

const MuslimInvestRequests = () => {
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const queryClient = useQueryClient();
  const [foundingTeam, setFoundingTeam] = useState<FoundingTeamData[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCompany, setSelectedCompany] =
    useState<CompanyProfileData | null>(null);
  const [filesUploaded, setFilesUploaded] = useState<any[]>([]);

  const { mutate: saveCompanyDetailsMutation } = useSaveCompanyProfile({
    onError: () => {
      toast.error("Unable to process, please try again later");
    },
  });
  const { data, isPending } = useGetActiveCompanies({ pageSize, page });
  const requests: CompanyProfileData[] = (data?.companies || []).filter(
    (c) => c.status !== "InProgress"
  );
  const totalRecords = data?.totalRecords || 0;
  const totalPages = Math.ceil(totalRecords / pageSize);

  const { data: pitchData } = useGetPitcheByCompanyId({
    id: selectedCompany?.id.toString() || "",
    enabled: !!selectedCompany,
  });

  const selectedPitch =
    pitchData && (pitchData.pitches[0]?.pitch as PitchFormData);

  const { mutate: sendMail } = useSendEmail({
    onSuccess() {},
  });

  const { data: teamMembersData, isLoading: isTeamLoading } =
    useGetPitchTeamMembers({
      id: selectedPitch?.id.toString() || "",
      enabled: !!selectedPitch,
    });

  const { data: pitchFilesData, isLoading: isFilesLoading } = useGetPitchFiles({
    id: selectedPitch?.id.toString() || "",
    enabled: !!selectedPitch,
  });

  useEffect(() => {
    if (pitchFilesData) {
      setFilesUploaded(pitchFilesData || []);
    }
  }, [pitchFilesData]);

  const downloadFile = async (fileId: number) => {
    try {
      const response = await axiosInstance.get(
        getAPIURL(`/Pitch/pitch-file/${fileId}/download`),
        { responseType: "blob" } // Important to get binary data
      );

      // Extract filename from headers if available
      const contentDisposition = response.headers["content-disposition"];
      const fileName = contentDisposition
        ? contentDisposition.split("filename=")[1]?.replace(/['"]/g, "")
        : `downloaded-file-${fileId}`;

      // Create a URL for the blob
      const fileURL = window.URL.createObjectURL(response.data);

      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Error downloading file:", err);
    }
  };

  const { mutate: deleteCompany } = useDeleteCompany({
    onSuccess: () => {
      toast.success("Company deleted successfully");
      setShowDeleteModal(false);
      queryClient.invalidateQueries({ queryKey: ["companies"] });
    },
    onError: () => {
      toast.error("Company to delete founder");
    },
  });

  const openModal = (company: CompanyProfileData) => {
    setSelectedCompany(company);
    setShowModal(true);
    getFoundingTeam(company.userId);
  };
  const closeModal = () => {
    setSelectedCompany(null);
    setShowModal(false);
  };

  const openDeleteModal = (company: CompanyProfileData) => {
    setSelectedCompany(company);
    setShowDeleteModal(true);
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    // Update React Query cache directly
    queryClient.setQueryData(
      ["companies", { pageSize, page }],
      (oldData: any) => {
        if (!oldData) return oldData;
        const updatedCompanies = oldData.companies.map(
          (c: CompanyProfileData) =>
            c.id === id ? { ...c, status: newStatus } : c
        );
        return { ...oldData, companies: updatedCompanies };
      }
    );

    // Update modal if open
    if (selectedCompany?.id === id) {
      setSelectedCompany({ ...selectedCompany, status: newStatus });
    }

    // Call mutation to save status
    const companyToUpdate = requests.find((c) => c.id === id);
    if (!companyToUpdate) return;

    const responseUser = await axiosInstance.get<Users>(
      getAPIURL(`/User/${companyToUpdate.userId}?id=${companyToUpdate.userId}`)
    );

    const formData = new FormData();
    Object.entries(companyToUpdate).forEach(([key, value]) => {
      if (value !== undefined && value !== null)
        formData.append(key, String(value));
    });
    formData.set("status", newStatus);
    saveCompanyDetailsMutation(formData, {
      onSuccess: () => {
        toast.success(`Company status updated to "${newStatus}"`);
        if (newStatus === "Rejected") {
          sendMail({
            email: responseUser?.data.user.email || "",
            subject: `Your Company Profile Has Been Rejected`,
            name: `${
              responseUser?.data.user.firstName +
                "" +
                responseUser?.data.user.lastName || ""
            }`,
            body: `<!DOCTYPE html>
          <html lang="en">
          <head>
          <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
          <title>Company Profile Status - SMBF</title>
          </head>
          <body style="margin:0; padding:0; font-family:Arial, Helvetica, sans-serif; background-color:#f4f6f8;">
          <table width="100%" border="0" cellspacing="0" cellpadding="0" bgcolor="#f4f6f8">
            <tr>
              <td align="center" style="padding:40px 20px;">
                <table width="600" border="0" cellspacing="0" cellpadding="0" bgcolor="#ffffff"
                  style="border-radius:10px; border:1px solid #e0e0e0; overflow:hidden;">
          
                  <!-- Header -->
                  <tr>
                    <td align="center" style="background: linear-gradient(221deg, #2366CD 0%, #3A97A4 100%); padding:30px;">
                      <img src="https://drive.google.com/uc?export=view&id=17bPec3b1DFm41gg757oKNyTmP1942MOg"
                        alt="SMBF" width="180"
                        style="max-width:180px; height:auto; display:block; margin:0 auto;" />
                    </td>
                  </tr>
          
                  <!-- Body -->
                  <tr>
                    <td style="padding:35px; text-align:left; color:#333333; font-size:15px; line-height:1.6;">
                      <h2 style="color:#2366CD; font-size:22px; margin:0 0 20px;">
                        Assalamu Alaikum ${
                          responseUser?.data.user.firstName || ""
                        },
                      </h2>
                      <p style="margin:0 0 15px;">
                        We regret to inform you that your company profile submitted to <b>SMBF / Muslim Invest</b> has been rejected.
                      </p>
                      <p style="margin:0 0 15px;">
                        Please review the feedback and contact our support team to resolve any issues or resubmit your profile.
                      </p>
                      <p style="text-align:center;">
                      <a href="https://connect.smbf.global" style="background: linear-gradient(221deg, #2366CD 0%, #3A97A4 100%); color:#ffffff; padding:14px 28px; font-size:16px; text-decoration:none; border-radius:6px; display:inline-block;">
                      Contact Support
                    </a>
                      </p>
                      <p style="margin:30px 0 0; font-size:13px; color:#555555;">
                        Best regards,<br/>The SMBF Team
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          
            <!-- Footer -->
            <tr>
              <td align="center" style="padding:20px; font-size:11px; color:#888888;">
                &copy; 2025 SMBF. All rights reserved.
              </td>
            </tr>
          </table>
          </body>
          </html>`,
          });
        }
        if (newStatus === "Accepted") {
          sendMail({
            email: responseUser?.data.user.email || "",
            subject: `Your Company Profile Has Been Accepted`,
            name: `${responseUser?.data.user.firstName || ""} ${
              responseUser?.data.user.lastName || ""
            }`,
            body: `
        <table width="100%" cellpadding="0" cellspacing="0" bgcolor="#f4f6f8" style="margin:0; padding:0;">
          <tr>
            <td align="center" style="padding:40px 20px;">
              <table width="600" cellpadding="0" cellspacing="0" bgcolor="#ffffff" style="border-radius:10px; border:1px solid #e0e0e0;">
                <!-- Header -->
                <tr>
                  <td align="center" style="background: linear-gradient(221deg, #2366CD 0%, #3A97A4 100%); padding:30px;">
                    <img src="https://drive.google.com/uc?export=view&id=17bPec3b1DFm41gg757oKNyTmP1942MOg" alt="SMBF" width="180" style="display:block; margin:0 auto;"/>
                  </td>
                </tr>
                <!-- Body -->
                <tr>
                  <td style="padding:35px; font-family:Arial, Helvetica, sans-serif; color:#333333; font-size:15px; line-height:1.6;">
                    <h2 style="color:#2366CD; font-size:22px; margin:0 0 20px;">
                      Assalamu Alaikum ${
                        responseUser?.data.user.firstName || ""
                      },
                    </h2>
                    <p style="margin:0 0 15px;">
                      Congratulations! Your company profile submitted to <b>SMBF / Muslim Invest</b> has been accepted.
                    </p>
                    <p style="margin:0 0 15px;">
                      Please log in to your account and proceed with the required payment to complete the next steps.
                    </p>
                    <p style="text-align:center;">
                      <a href="https://connect.smbf.global" style="background: linear-gradient(221deg, #2366CD 0%, #3A97A4 100%); color:#ffffff; padding:14px 28px; font-size:16px; text-decoration:none; border-radius:6px; display:inline-block;">
                        Login & Make Payment
                      </a>
                    </p>
                    <p style="margin:30px 0 0; font-size:13px; color:#555555;">
                      Best regards,<br/>The SMBF Team
                    </p>
                  </td>
                </tr>
                <!-- Footer -->
                <tr>
                  <td align="center" style="padding:20px; font-size:11px; color:#888888;">
                    &copy; 2025 SMBF. All rights reserved.
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
        `,
          });
        }
      },
    });
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  const handleDelete = (id: number) => {
    deleteCompany(id);
  };

  const getFoundingTeam = async (id: string) => {
    try {
      const response = await axiosInstance.get<FoundingTeamData[]>(
        getAPIURL(`/MuslimInvestProfile/FoundingTeam/by-user/${id}`)
      );
      setFoundingTeam(response.data ?? []);
    } catch (err) {
      console.error("Failed to fetch founding team:", err);
    } finally {
      // setLoading(false);
    }
  };

  return (
    <div className="container py-4">
      <h2 className="fw-bold mb-4">Muslim Invest – Company Requests</h2>

      {isPending && (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {!isPending && requests.length === 0 && (
        <div className="alert alert-info text-center">
          No company requests found
        </div>
      )}

      {!isPending && requests.length > 0 && (
        <>
          <Table
            striped
            bordered
            hover
            responsive
            className="rounded-4 shadow-sm"
          >
            <thead className="table-light">
              <tr>
                <th>ID</th>
                <th>Company Name</th>
                <th>Description</th>
                <th>Stage</th>
                <th>Status</th>
                <th>Sectors</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((company) => (
                <tr key={company.id}>
                  <td>{company.id}</td>
                  <td>{company.companyName}</td>
                  <td>{company.companyDescription || "-"}</td>
                  <td>{company.developmentStage || "-"}</td>
                  {company.status === "Live" ? (
                    <td>Live</td>
                  ) : (
                    <td>
                      <select
                        className="form-select"
                        value={company.status}
                        onChange={(e) =>
                          handleStatusChange(company.id, e.target.value)
                        }
                      >
                        <option value="Awaiting Approval">
                          Awaiting Approval
                        </option>
                        <option value="Accepted">Accepted</option>
                        <option value="Rejected">Rejected</option>
                      </select>
                    </td>
                  )}
                  <td>{company.sectorsOfInterest || "-"}</td>
                  <td className="d-flex gap-2">
                    <BsEye
                      size={20}
                      className="text-primary cursor-pointer"
                      onClick={() => openModal(company)}
                    />
                    {/* <BsTrash
                      size={20}
                      className="text-danger cursor-pointer"
                      onClick={() => openDeleteModal(company)}
                    /> */}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="d-flex justify-content-center mt-3">
            <Pagination>
              <Pagination.Prev
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              />
              {Array.from({ length: totalPages }, (_, i) => (
                <Pagination.Item
                  key={i + 1}
                  active={i + 1 === page}
                  onClick={() => handlePageChange(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
              />
            </Pagination>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCompany && (
            <p>
              Are you sure you want to delete{" "}
              <strong>{selectedCompany.companyDescription}</strong>?
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            className="rounded-pill"
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() =>
              selectedCompany?.id && handleDelete(selectedCompany.id)
            }
            className="rounded-pill"
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal */}
      <Modal show={showModal} onHide={closeModal} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Company Details</Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            maxHeight: "65vh",
            overflowY: "auto",
          }}
        >
          {selectedCompany && (
            <div className="p-2">
              {/* Status Dropdown at Top */}
              {selectedCompany.status === "Live" ? (
                <></>
              ) : (
                <select
                  className="form-select"
                  value={selectedCompany.status}
                  onChange={(e) =>
                    handleStatusChange(selectedCompany.id, e.target.value)
                  }
                >
                  <option value="Awaiting Approval">Awaiting Approval</option>
                  <option value="Accepted">Accepted</option>
                  <option value="Rejected">Rejected</option>
                </select>
              )}

              {/* Company Details */}
              <div className="card shadow-sm border-0 mb-3 p-3">
                <h5 className="fw-semibold mb-3">Company Details</h5>
                <Row>
                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaBuilding
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Company Name</strong>
                        <div className="text-muted small">
                          {selectedCompany.companyName || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaGlobe size={20} className="me-2 mt-1 text-secondary" />
                      <div>
                        <strong>Country of Registration</strong>
                        <div className="text-muted small">
                          {selectedCompany.countryOfRegistration || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaBuilding
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Description</strong>
                        <div className="text-muted small">
                          {selectedCompany.companyDescription || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaInfoCircle
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Mission Statement</strong>
                        <div className="text-muted small">
                          {selectedCompany.missionStatement || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaIndustry
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Vision</strong>
                        <div className="text-muted small">
                          {selectedCompany.vision || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaBullseye
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Development Stage</strong>
                        <div className="text-muted small">
                          {selectedCompany.developmentStage || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Offerings / Financials */}
              <div className="card shadow-sm border-0 mb-3 p-3">
                <h5 className="fw-semibold mb-3">Offerings & Financials</h5>
                <Row>
                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaBoxes size={20} className="me-2 mt-1 text-secondary" />
                      <div>
                        <strong>Product/Service</strong>
                        <div className="text-muted small">
                          {selectedCompany.productServiceDescription || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaBoxOpen
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Differentiation</strong>
                        <div className="text-muted small">
                          {selectedCompany.productMarketDifferentiation || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaBullseye
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Target Market</strong>
                        <div className="text-muted small">
                          {selectedCompany.targetMarket || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaGlobe size={20} className="me-2 mt-1 text-secondary" />
                      <div>
                        <strong>Landscape</strong>
                        <div className="text-muted small">
                          {selectedCompany.landscape || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaDollarSign
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Amount Sought</strong>
                        <div className="text-muted small">
                          {selectedCompany.amountSought || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaChartLine
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Revenue Projections</strong>
                        <div className="text-muted small">
                          {selectedCompany.revenueProjections || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaPercentage
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Profit Margins</strong>
                        <div className="text-muted small">
                          {selectedCompany.profitMargins || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaTag size={20} className="me-2 mt-1 text-secondary" />
                      <div>
                        <strong>Sectors</strong>
                        <div className="text-muted small">
                          {selectedCompany.sectorsOfInterest || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
              <div className="card shadow-sm border-0 mb-3 p-3">
                <h5 className="fw-semibold mb-3">Founding Team</h5>
                <div className="row">
                  {Array.isArray(foundingTeam) &&
                    foundingTeam.map((founder) => (
                      <div
                        className="col-12 col-sm-6 col-md-4 col-lg-3 mb-4"
                        key={founder.id ?? `${founder.email}-${Math.random()}`}
                      >
                        <div
                          className="card shadow-sm position-relative border-0"
                          style={{
                            borderRadius: "14px",
                            transition: "0.2s ease-in-out",
                            paddingTop: "45px",
                          }}
                        >
                          <div className="card-body text-center">
                            <h5 className="fw-semibold mb-1">
                              {founder.firstName} {founder.lastName}
                            </h5>

                            <h6
                              className="text-muted mb-3"
                              style={{ fontSize: "0.9rem" }}
                            >
                              {founder.role}
                            </h6>

                            <p className="small mb-1">
                              <strong>Email:</strong>
                              <br />
                              {founder.email}
                            </p>

                            <p className="small mb-0">
                              <strong>LinkedIn:</strong>
                              <br />
                              <a
                                href={founder.linkedIn}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View Profile
                              </a>
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
              <div className="card shadow-sm border-0 mb-3 p-3">
                <h5 className="fw-semibold mb-3">Pitch Details</h5>
                <Row>
                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaBuilding
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Sector</strong>
                        <div className="text-muted small">
                          {selectedPitch?.sector || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaGlobe size={20} className="me-2 mt-1 text-secondary" />
                      <div>
                        <strong>Region</strong>
                        <div className="text-muted small">
                          {selectedPitch?.region || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaBullseye
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Investment Type</strong>
                        <div className="text-muted small">
                          {selectedPitch?.investmentType || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaDollarSign
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Investment Ticket Size</strong>
                        <div className="text-muted small">
                          {selectedPitch?.investmentTicketSize || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaDollarSign
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Amount Sought</strong>
                        <div className="text-muted small">
                          ${selectedPitch?.amountSought || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={12} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaInfoCircle
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Description</strong>
                        <div className="text-muted small">
                          {selectedPitch?.companyDescription || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaIndustry
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Mission</strong>
                        <div className="text-muted small">
                          {selectedPitch?.mission || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaIndustry
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Vision</strong>
                        <div className="text-muted small">
                          {selectedPitch?.vision || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaBullseye
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Market Problem</strong>
                        <div className="text-muted small">
                          {selectedPitch?.identifiedMarketProblem || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaBullseye
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Solution</strong>
                        <div className="text-muted small">
                          {selectedPitch?.solution || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaUsers size={20} className="me-2 mt-1 text-secondary" />
                      <div>
                        <strong>Target Audience</strong>
                        <div className="text-muted small">
                          {selectedPitch?.targetAudience || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaDollarSign
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Revenue Streams</strong>
                        <div className="text-muted small">
                          {selectedPitch?.revenueStreams || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaChartLine
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Pricing Strategy</strong>
                        <div className="text-muted small">
                          {selectedPitch?.pricingStrategy || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaChartLine
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Revenue Forecast</strong>
                        <div className="text-muted small">
                          {selectedPitch?.revenueForecast || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaPercentage
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Profit Margins</strong>
                        <div className="text-muted small">
                          {selectedPitch?.profitMargins || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={6} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaBullseye
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Break-Even Margins</strong>
                        <div className="text-muted small">
                          {selectedPitch?.breakEvenMargins || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={12} className="mb-3">
                    <div className="d-flex align-items-start">
                      <FaDollarSign
                        size={20}
                        className="me-2 mt-1 text-secondary"
                      />
                      <div>
                        <strong>Use of Funds</strong>
                        <div className="text-muted small">
                          {selectedPitch?.useOfFunds || "-"}
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Team Members */}
              <div className="card shadow-sm border-0 mb-3 p-3">
                <h5 className="fw-semibold mb-3">Team Members</h5>
                {isTeamLoading ? (
                  <div className="text-center py-2">
                    <Spinner animation="border" size="sm" /> Loading team
                    members...
                  </div>
                ) : teamMembersData?.teamMembers?.length ? (
                  <Row>
                    {teamMembersData.teamMembers.map((member) => (
                      <Col md={3} sm={6} className="mb-3" key={member.id}>
                        <Card className="shadow-sm border-0 h-100 rounded-3 text-center">
                          <Card.Body>
                            <Card.Title className="fw-semibold mb-1 text-dark">
                              {member.firstName} {member.lastName}
                            </Card.Title>
                            <Card.Subtitle className="text-muted mb-2">
                              {member.role}
                            </Card.Subtitle>
                            <Card.Text className="small text-secondary mb-0">
                              <strong>Experience:</strong>{" "}
                              {member.experience || "N/A"}
                            </Card.Text>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div className="text-center text-muted">
                    No team members found
                  </div>
                )}
              </div>

              {/* Pitch Files */}
              <div className="card shadow-sm border-0 mb-3 p-3">
                <h5 className="fw-semibold mb-3">Pitch Files</h5>
                {isFilesLoading ? (
                  <div className="text-center py-2">
                    <Spinner animation="border" size="sm" /> Loading files...
                  </div>
                ) : filesUploaded.length ? (
                  <Table
                    striped
                    bordered
                    hover
                    responsive
                    className="align-middle shadow-sm"
                  >
                    <thead className="table-light">
                      <tr>
                        <th>File Name</th>
                        <th>Type</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filesUploaded.map((file) => (
                        <tr key={file.id}>
                          <td>
                            <FaFilePdf className="me-2 text-danger" />
                            {file.name}
                          </td>
                          <td>{file.type}</td>
                          <td>
                            <Button
                              variant="primary"
                              size="sm"
                              className="border-0 rounded-circle me-2"
                              style={{ width: "30px", height: "30px" }}
                              onClick={() => downloadFile(file.id)}
                            >
                              <BsFileArrowDown />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center text-muted">No files found</div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeModal}
            className="rounded-pill"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MuslimInvestRequests;
